// PersonPicker — choose one person or several from a list (spec §6.1): the
// project lead, a project's members, a work item's assignee, the filters.
//
// A trigger button opens a usePopover panel holding a search box and a
// listbox. The search box is the WAI-ARIA combobox: focus stays in it while
// ArrowUp/Down, Home/End move the active option (aria-activedescendant) and
// Enter picks it. Escape closes and hands focus back to the trigger; so does a
// single pick. A multi picker stays open, so several people can be ticked in a
// row. Who is in `people` is the caller's business (project_people, the
// project's members); this only shows and picks.
import { useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { usePopover } from './dialog';
import { Avatar, Button, Icon } from './primitives';

export type Person = {
  id: string;
  name: string;
  /** A second line: their job title, or why they can't be picked as a lead. */
  title?: string | null;
};

type Single = {
  multiple?: false;
  value: string | null;
  onChange: (id: string | null) => void;
  /** Offer "Nobody" at the top, which picks null. */
  noneLabel?: string;
};
type Multi = {
  multiple: true;
  value: string[];
  onChange: (ids: string[]) => void;
  noneLabel?: never;
};

export function PersonPicker({
  people,
  placeholder = 'Pick a person',
  ariaLabel,
  id,
  disabled,
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
  quiet,
  ...pick
}: (Single | Multi) & {
  people: Person[];
  /** No frame until hover or focus: a value in a field list (the work item view). */
  quiet?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  /** From a Field's render form, so its <label htmlFor> names the trigger. */
  id?: string;
  disabled?: boolean;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  'aria-required'?: boolean;
}) {
  const pop = usePopover({ maxH: 320, minWidth: 260 });
  const { open, show, close } = pop;
  const listId = useId();
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const searchRef = useRef<HTMLInputElement>(null);

  const chosen = pick.multiple ? pick.value : pick.value ? [pick.value] : [];
  const options = useMemo(() => {
    const s = q.trim().toLowerCase();
    const hits = people.filter(
      (p) => !s || p.name.toLowerCase().includes(s) || (p.title ?? '').toLowerCase().includes(s),
    );
    const none: Person[] =
      !pick.multiple && pick.noneLabel && !s ? [{ id: '', name: pick.noneLabel }] : [];
    return [...none, ...hits];
  }, [people, q, pick.multiple, pick.noneLabel]);
  const lit = Math.min(active, options.length - 1);
  const optId = (i: number) => `${listId}-${i}`;

  const openPanel = () => {
    setQ('');
    setActive(0);
    show();
    // After the panel mounts: the search box takes the keys.
    requestAnimationFrame(() => searchRef.current?.focus());
  };
  const choose = (p: Person | undefined) => {
    if (!p) return;
    if (pick.multiple) {
      pick.onChange(chosen.includes(p.id) ? chosen.filter((x) => x !== p.id) : [...chosen, p.id]);
      return;
    }
    pick.onChange(p.id || null);
    close();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const last = options.length - 1;
    const to = (i: number) => {
      e.preventDefault();
      setActive(Math.max(0, Math.min(last, i)));
    };
    if (e.key === 'ArrowDown') return to(lit + 1);
    if (e.key === 'ArrowUp') return to(lit - 1);
    if (e.key === 'Home') return to(0);
    if (e.key === 'End') return to(last);
    if (e.key === 'Enter') {
      e.preventDefault();
      choose(options[lit]);
    }
  };

  const one = !pick.multiple ? people.find((p) => p.id === pick.value) : undefined;
  const label = pick.multiple
    ? placeholder
    : one
      ? one.name
      : pick.value === null && pick.noneLabel
        ? pick.noneLabel
        : placeholder;

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <button
        {...pop.triggerProps}
        type="button"
        id={id}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-describedby={describedBy}
        aria-invalid={invalid}
        onClick={() => (open ? close() : openPanel())}
        className={quiet ? 'hf-row-link hf-hit' : 'hf-select hf-hit'}
        style={{
          width: quiet ? 'calc(100% + 16px)' : '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: quiet ? 'transparent' : 'var(--hf-surface)',
          border: `1px solid ${open ? 'var(--hf-accent-line)' : quiet ? 'transparent' : 'var(--hf-rule-control)'}`,
          borderRadius: quiet ? 4 : 8,
          color: one ? (quiet ? 'var(--hf-accent-text)' : 'var(--hf-ink)') : 'var(--hf-ink-3)',
          fontSize: quiet ? 12.5 : 13,
          fontFamily: 'inherit',
          padding: quiet ? '4px 8px' : '7px 11px',
          margin: quiet ? '-4px -8px' : undefined,
          textAlign: 'left',
        }}
      >
        {pick.multiple ? (
          <Icon name="plus" size={13} />
        ) : one ? (
          <Avatar name={one.name} size={20} />
        ) : (
          <Icon name="user" size={14} />
        )}
        <span
          style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {label}
        </span>
        {!quiet && (
          <Icon name="chevronDown" size={13} style={{ color: 'var(--hf-ink-3)', flexShrink: 0 }} />
        )}
      </button>
      {open && (
        <div
          {...pop.panelProps}
          style={{
            ...pop.panelProps.style,
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--hf-space-1)',
            padding: 'var(--hf-space-1)',
            borderRadius: 'var(--hf-radius-card)',
            background: 'var(--hf-paper-2)',
            color: 'var(--hf-ink)',
            border: '1px solid var(--hf-rule-2)',
            boxShadow: 'var(--hf-shadow-pop)',
          }}
        >
          <div
            className="hf-field"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: 'var(--hf-pad-field)',
            }}
          >
            <Icon name="search" size={13} style={{ color: 'var(--hf-ink-3)', flexShrink: 0 }} />
            <input
              ref={searchRef}
              role="combobox"
              aria-label="Search people"
              aria-expanded="true"
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={lit >= 0 ? optId(lit) : undefined}
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setActive(0);
              }}
              onKeyDown={onKeyDown}
              placeholder="Search people…"
              autoComplete="off"
              style={{
                flex: 1,
                minWidth: 0,
                background: 'transparent',
                border: 0,
                color: 'var(--hf-ink)',
                font: 'inherit',
                fontSize: 'var(--hf-text-sm)',
              }}
            />
          </div>
          <div
            id={listId}
            role="listbox"
            aria-label={ariaLabel ?? placeholder}
            aria-multiselectable={pick.multiple || undefined}
            // Options take no focus: a press leaves it in the search box.
            onMouseDown={(e) => e.preventDefault()}
            style={{ overflowY: 'auto', minHeight: 0 }}
          >
            {options.length === 0 && (
              <div
                role="status"
                style={{
                  padding: 'var(--hf-space-2) var(--hf-space-3)',
                  fontSize: 'var(--hf-text-sm)',
                  color: 'var(--hf-ink-3)',
                }}
              >
                No one matches “{q.trim()}”
              </div>
            )}
            {options.map((p, i) => {
              const on = p.id ? chosen.includes(p.id) : pick.value === null;
              return (
                <button
                  key={p.id || 'none'}
                  id={optId(i)}
                  type="button"
                  role="option"
                  aria-selected={on}
                  tabIndex={-1}
                  onClick={() => choose(p)}
                  onMouseMove={() => i !== lit && setActive(i)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--hf-space-2)',
                    padding: 'var(--hf-space-2) var(--hf-space-3)',
                    borderRadius: 'var(--hf-radius-md)',
                    border: 0,
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontFamily: 'inherit',
                    background: i === lit ? 'var(--hf-surface-2)' : 'transparent',
                    color: on ? 'var(--hf-accent-text)' : 'var(--hf-ink-2)',
                  }}
                >
                  {p.id ? <Avatar name={p.name} size={22} /> : <Icon name="user" size={16} />}
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 'var(--hf-text-sm)' }}>
                      {p.name}
                    </span>
                    {p.title && (
                      <span
                        style={{
                          display: 'block',
                          fontSize: 'var(--hf-text-2xs)',
                          color: 'var(--hf-ink-3)',
                        }}
                      >
                        {p.title}
                      </span>
                    )}
                  </span>
                  {on && <Icon name="check" size={13} style={{ flexShrink: 0 }} />}
                </button>
              );
            })}
          </div>
          {pick.multiple && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => close()}
              style={{ alignSelf: 'flex-end' }}
            >
              Done
            </Button>
          )}
          <span className="hf-sr-only" aria-live="polite">
            {pick.multiple ? `${chosen.length} chosen` : ''}
          </span>
        </div>
      )}
    </div>
  );
}
